import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TableCell } from '@mui/material';

// COMPONENTS
import BoxGrid from '@/main/components/shared/boxGrid/boxGrid';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import { KOGTableRow } from '@/main/components/shared/boxGrid/styles';

// SERVICES
import { RoutesConstants } from '@/main/routes/routesConstants';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import {
  getDefaultMessageResultsFound,
  getTranslateByKey,
  haveIgnoreElement
} from '@/main/services/utils';

import * as S from './styles';
import { TextEllipsis } from '@/main/components/shared/styles';
import { supervisorApi } from '@/main/api';
import { SupervisorFilterRequest } from '@/main/models/request/supervisorRequest';
import { StatusUserGroupEnum } from '@/main/services/enum/StatusUserGroupEnum';
import { toast } from 'react-toastify';
export interface IGridData {
  isLoading: boolean;
  totalItems: number;
  data: any[];
}

export const INITIAL_GRID_STATE: IGridData = {
  isLoading: false,
  totalItems: 0,
  data: []
};

interface ISupervisorSearchGridProps {
  gridData: IGridData;
  setGridData: React.Dispatch<React.SetStateAction<IGridData>>;
  formFilters: any;
}

const SupervisorSearchGrid: React.FC<ISupervisorSearchGridProps> = ({
  gridData,
  setGridData,
  formFilters
}) => {
  const navigate = useNavigate();
  const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState(false);
  const [hasShowMore, setHasShowMore] = useState(false);
  const [pageIndex, setPageIndex] = useState(PaginationEnum.PageIndexIntial);

  function handleRowBoxGrid(
    supervisor: any,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) {
    if (haveIgnoreElement(event.target as HTMLElement)) return;
     if(supervisor.hasOtherCompanyRelation){
             toast.error(getTranslateByKey('information.supervisorAssociatedWithAnotherInsurance', true));
             return;
            }
    navigate(
      `${RoutesConstants.registrationRegisterSupervisor}?supervisorGuid=${supervisor.guid}`
    );
  }

  function onLoadInfiniteScroll(request: SupervisorFilterRequest) {
    setIsLoadingInfiniteScroll(true);

    const requestSupervisor = {
      ...request,
      pageIndex: request.pageIndex ?? PaginationEnum.PageIndexIntial,
      pageSize: request.pageSize ?? PaginationEnum.PageSize30
    };

    supervisorApi.getSupervisor(requestSupervisor).then(response => {
      let resultData: any[] = [];
      let resultTotalItems: number = 0;
      if (response.data) {
        resultData = response.data.results;
        resultTotalItems = response.data.totalNumberOfRecords;
      }

      const newData = resultData.filter(
        (item: any) => !gridData.data.some((existing: any) => existing.guid === item.guid)
      );
      const updatedData = [...gridData.data, ...newData];
      setGridData({
        isLoading: false,
        totalItems: resultTotalItems,
        data: updatedData
      });
    }).finally(() => setIsLoadingInfiniteScroll(false));
  }

  function onLoadSearchGrid() {
    setPageIndex(prev => prev + 1);
    const formFiltersOnLoadSearchGrid = formFilters;
    if (formFiltersOnLoadSearchGrid) {
      const updatedFormFilters = {
        ...formFiltersOnLoadSearchGrid,
        pageIndex: pageIndex + 1
      };
      onLoadInfiniteScroll(updatedFormFilters);
    }
  }

  useEffect(() => {
    setHasShowMore(gridData.data.length < gridData.totalItems);
  }, [gridData.data, gridData.totalItems]);

  useEffect(() => {
    setPageIndex(PaginationEnum.PageIndexIntial);
  }, [formFilters]);

  const titleGrid = getDefaultMessageResultsFound(
    gridData.data.length,
    gridData.totalItems
  );

  const rowHeaders = [
    getTranslateByKey('information.rfc'),
    getTranslateByKey('information.name'),
    getTranslateByKey('information.state') + ' / ' + getTranslateByKey('information.city'),
    getTranslateByKey('information.status')
  ];

  const sizeColumns = [
    '150px',
    '40%',
    '30%',
    '160px'
  ];

  function renderColor(active: boolean) {
    switch (active) {
      case true:
        return '#00cc66';
      case false:
        return '#0099ff';
      default:
        return '#00cc66';
    }
  }

  if (gridData.isLoading) return <LoadingComponent />;
  if (gridData.data.length === 0 && formFilters === null) return null;
  if (gridData.data.length === 0 && formFilters !== null)
    return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
  if (gridData.data.length === 0 && !gridData.isLoading) return null;

  return (
    <S.Container>
      <BoxGrid
        title={titleGrid}
        sizeColumns={sizeColumns}
        rowHeaders={rowHeaders}
        infiniteScrollConfig={{
          dataLength: gridData.data.length,
          fetchData: () => onLoadSearchGrid(),
          hasMore: hasShowMore,
          isLoading: isLoadingInfiniteScroll
        }}>
        {gridData.data.map(supervisor => (
          <KOGTableRow
            key={supervisor.guid}
            id={`id-searchSupervisor-tableRow-result-${supervisor.registrationNumber}`}
            onClick={e => handleRowBoxGrid(supervisor, e)}>
            <TableCell>
              <TextEllipsis title={supervisor.rfc}>
                {supervisor.registrationNumber}
              </TextEllipsis>
            </TableCell>
            <TableCell>
              <TextEllipsis title={supervisor.name}>
                {supervisor.name}
              </TextEllipsis>
            </TableCell>
            <TableCell>
              <TextEllipsis title={`${supervisor.addresses[0]?.municipality?.city?.state?.name} / ${supervisor.addresses[0]?.municipality?.city?.name}`}>
                {supervisor.addresses
                  && `${supervisor.addresses[0]?.municipality?.city?.state?.name} / ${supervisor.addresses[0]?.municipality?.city?.name}`}
              </TextEllipsis>
            </TableCell>
            <TableCell id='id-searchSupervisor-icon-status'>
              <S.AreaStatus>
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    backgroundColor: renderColor(supervisor?.statusRegister?.idStatusRegister),
                    height: 30,
                    width: 100,
                    borderRadius: 5,
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    color: '#fff',
                    fontWeight: 500
                  }}>
                  {supervisor?.statusRegister?.idStatusRegister === StatusUserGroupEnum.Active
                    ? getTranslateByKey('statusRegister.active')
                    : getTranslateByKey('statusRegister.suspend')}
                </div>
              </S.AreaStatus>
            </TableCell>
          </KOGTableRow>
        ))}
      </BoxGrid>
    </S.Container>
  );
};

export default SupervisorSearchGrid;